import '../css/main.scss';
import * as bootstrap from 'bootstrap';
import LenisImport from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper/swiper-bundle.min.js';
import 'swiper/swiper-bundle.min.css';
import { DragCarousel } from './DraggableCarousel';
import { initFilteredGrid } from './blocks/filtered-grid';
import { initFooterLanguageSwitcher } from './blocks/footer';

// Expose Bootstrap globally if needed for interactive components
window.bootstrap = bootstrap;

// Robust Lenis Constructor handling for Webpack 4 module interop
const Lenis = LenisImport.default || LenisImport;

// Register GSAP Plugins
gsap.registerPlugin(ScrollTrigger);

document.addEventListener('DOMContentLoaded', () => {
  // Initialize Lenis Smooth Scroll
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
  });

  // Sync Lenis scroll with GSAP ScrollTrigger
  lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });

  gsap.ticker.lagSmoothing(0);

  // 1. PREPARE HERO TIMELINE & SET INITIAL HIDDEN STATES IMMEDIATELY ON HERO-TEXT-INNER
  let heroTl = null;
  const isExperiencesPage =
    document.body.classList.contains('page-template-experiences') ||
    document.body.classList.contains('page-esperienze') ||
    document.body.classList.contains('post-type-archive-tribe_events') ||
    document.body.classList.contains('page-eventi') ||
    document.querySelector('.hero-esperienze') ||
    document.querySelector('.hero-evento') ||
    document.querySelector('.evento-content');

  if (isExperiencesPage) {
    const textInners = Array.from(document.querySelectorAll('.hero-esperienze .hero-text-inner, .hero-evento .hero-text-inner, .evento-content .hero-text-inner'));
    const heroImg = document.querySelector('.hero-image-wrapper img');
    const heroImageWrapper = document.querySelector('.hero-image-wrapper');
    const gallerySection = document.querySelector('.dettaglio-gallery-section');

    // Create paused timeline
    heroTl = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } });

    // Set initial hidden positions IMMEDIATELY (115% ensures text is fully outside the mask avoiding peeking letters)
    if (textInners.length > 0) gsap.set(textInners, { yPercent: 115 });
    if (heroImg) gsap.set(heroImg, { opacity: 0, scale: 1.25 });
    if (gallerySection && !heroImg) gsap.set(gallerySection, { opacity: 0 });

    // Check if hero image is placed before text (e.g. detail hero section)
    const isImageFirst =
      heroImageWrapper &&
      heroImageWrapper.parentElement &&
      heroImageWrapper.parentElement.firstElementChild === heroImageWrapper;

    if (isImageFirst) {
      if (heroImg) {
        heroTl.to(heroImg, {
          opacity: 1,
          scale: 1,
          duration: 2.0,
          ease: 'power2.out',
        });
      }
      if (textInners.length > 0) {
        heroTl.to(
          textInners,
          {
            yPercent: 0,
            stagger: 0.18,
            duration: 1.4,
          },
          '-=1.3'
        );
      }
    } else {
      if (textInners.length > 0) {
        heroTl.to(textInners, {
          yPercent: 0,
          stagger: 0.18,
          duration: 1.4,
        });
      }
      if (heroImg) {
        heroTl.to(
          heroImg,
          {
            opacity: 1,
            scale: 1,
            duration: 2.0,
            ease: 'power2.out',
          },
          '-=1.1'
        );
      }
      if (gallerySection && !heroImg) {
        heroTl.to(
          gallerySection,
          {
            opacity: 1,
            duration: 1.4,
            ease: 'power2.out',
          },
          '<'
        );
      }
    }
  } else if (document.querySelector('.hero-home')) {
    const heroHomeSection = document.querySelector('.hero-home');
    const heroHomeContainer = document.querySelector('.hero-home-container');
    const homeTextInners = Array.from(document.querySelectorAll('.hero-home .hero-text-inner'));

    // Create paused timeline for home hero intro
    heroTl = gsap.timeline({ paused: true });

    // Set initial states IMMEDIATELY (Full screen, 0 radius, hidden text)
    gsap.set(heroHomeSection, { padding: '0rem' });
    gsap.set(heroHomeContainer, { borderRadius: '0px' });
    if (homeTextInners.length > 0) {
      gsap.set(homeTextInners, { yPercent: 115 });
    }

    // Use GSAP matchMedia API for responsive breakpoint (< 992px)
    const mm = gsap.matchMedia();

    heroTl.to(heroHomeSection, {
      padding: () => {
        let paddingVal = '1.5rem';
        mm.add('(max-width: 991.98px)', () => {
          paddingVal = '0.5rem';
        });
        return paddingVal;
      },
      duration: 1.8,
      ease: 'power3.inOut',
    });

    window.addEventListener('resize', () => {
      if (heroTl && heroTl.progress() === 1) {
        gsap.set(heroHomeSection, {
          padding: window.innerWidth < 992 ? '0.5rem' : '1.5rem',
        });
      }
    });

    heroTl.to(
      heroHomeContainer,
      {
        borderRadius: '8px',
        duration: 1.8,
        ease: 'power3.inOut',
      },
      '<'
    );

    // Step 2: Text reveal animation (matches esperienze.html text-mask animation)
    if (homeTextInners.length > 0) {
      heroTl.to(
        homeTextInners,
        {
          yPercent: 0,
          stagger: 0.2,
          duration: 1.3,
          ease: 'power3.out',
        },
        '-=1.0'
      );
    }
  } else if (document.querySelector('.storie-hero-section')) {
    const storieHeroSection = document.querySelector('.storie-hero-section');
    const storieHeroImg = storieHeroSection.querySelector('.storie-hero-img');
    const storieTextInners = Array.from(storieHeroSection.querySelectorAll('.hero-text-inner'));

    // Create paused timeline
    heroTl = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } });

    // Set initial states IMMEDIATELY: img scale 1.25, text inners yPercent 115
    if (storieHeroImg) {
      gsap.set(storieHeroImg, { scale: 1.25 });
    }
    if (storieTextInners.length > 0) {
      gsap.set(storieTextInners, { yPercent: 115 });
    }

    // Animate image to scale 1
    if (storieHeroImg) {
      heroTl.to(storieHeroImg, {
        scale: 1,
        duration: 2.0,
        ease: 'power2.out',
      });
    }

    // Animate text inners to y 0
    if (storieTextInners.length > 0) {
      heroTl.to(
        storieTextInners,
        {
          yPercent: 0,
          stagger: 0.18,
          duration: 1.3,
          ease: 'power3.out',
        },
        '-=1.5'
      );
    }
  }

  // Header ScrollTrigger: translateY(1.5rem) -> translateY(0) on scroll
  const headerTransparent = document.querySelector('.site-header-transparent');
  if (headerTransparent) {
    gsap.to(headerTransparent, {
      y: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero-home',
        start: 'top top',
        end: '+=100',
        scrub: true,
      },
    });
  }

  // Sticky & Smart Hide/Show Header on Lenis Scroll (> 100px)
  const siteHeader = document.querySelector('.site-header');
  const siteOverlay = document.querySelector('.site-menu-overlay');

  let isSubmenuOpen = false;
  let isMobileDrawerOpen = false;
  let activeSubmenuId = null;

  if (siteHeader) {
    let lastScrollY = 0;
    const scrollThreshold = 100;

    const handleHeaderScroll = (scrollPos) => {
      if (isSubmenuOpen || isMobileDrawerOpen) return; // Prevent header hiding while submenu or mobile drawer is open

      const currentScrollY = Math.max(0, scrollPos);

      if (currentScrollY > scrollThreshold) {

        siteHeader.classList.add('is-sticky');

        // Direction check with 5px tolerance threshold to prevent jitter
        if (currentScrollY > lastScrollY) {
          // Scrolling DOWN -> Hide Header (-100%)
          siteHeader.classList.add('is-hidden');

        } else if (currentScrollY < lastScrollY) {
          // Scrolling UP -> Show Header (0)
          siteHeader.classList.remove('is-hidden');
        }
      } else {
        // Back near top (<= 100px) -> Remove sticky & hidden classes
        siteHeader.classList.remove('is-sticky', 'is-hidden');
      }

      lastScrollY = currentScrollY;
    };

    // Attach to Lenis scroll event
    lenis.on('scroll', (e) => {
      handleHeaderScroll(e.scroll);
    });

    // Check initial scroll state on load
    handleHeaderScroll(window.scrollY || 0);
  }

  // Mega Submenu Toggle & Lenis Scroll Lock/Unlock (Supports any data-submenu)
  const desktopSubmenuLinks = document.querySelectorAll('.nav-link[data-submenu]');
  const megaSubmenus = document.querySelectorAll('.mega-submenu');
  let switchTimeout = null;

  if (siteOverlay && siteHeader) {
    const closeSubmenu = () => {
      if (switchTimeout) {
        clearTimeout(switchTimeout);
        switchTimeout = null;
      }
      isSubmenuOpen = false;
      activeSubmenuId = null;
      desktopSubmenuLinks.forEach(link => link.classList.remove('active'));
      megaSubmenus.forEach(menu => menu.classList.remove('is-active'));
      siteOverlay.classList.remove('is-active');
      siteHeader.classList.remove('is-menu-open');

      const currentScroll = (lenis && typeof lenis.scroll !== 'undefined') ? lenis.scroll : (window.scrollY || 0);
      if (currentScroll <= 100) {
        siteHeader.classList.remove('is-sticky');
      }
      lenis.start(); // Resume Lenis scroll when submenu is closed
    };

    const openSubmenu = (targetId, navLink) => {
      const targetSubmenu = document.getElementById(`submenu-${targetId}`);
      if (!targetSubmenu) return;

      if (switchTimeout) {
        clearTimeout(switchTimeout);
        switchTimeout = null;
      }

      if (isSubmenuOpen && activeSubmenuId && activeSubmenuId !== targetId) {
        // Switching submenus: Fade out current active submenu first, then fade in target
        desktopSubmenuLinks.forEach(link => link.classList.remove('active'));
        megaSubmenus.forEach(menu => menu.classList.remove('is-active'));

        activeSubmenuId = targetId;
        navLink.classList.add('active');

        switchTimeout = setTimeout(() => {
          if (!isSubmenuOpen || activeSubmenuId !== targetId) return;
          targetSubmenu.classList.add('is-active');
          switchTimeout = null;
        }, 220);
      } else {
        // Opening submenu directly when none is open
        desktopSubmenuLinks.forEach(link => link.classList.remove('active'));
        megaSubmenus.forEach(menu => menu.classList.remove('is-active'));

        isSubmenuOpen = true;
        activeSubmenuId = targetId;
        navLink.classList.add('active');
        targetSubmenu.classList.add('is-active');
        siteOverlay.classList.add('is-active');
        siteHeader.classList.add('is-sticky', 'is-menu-open');
        siteHeader.classList.remove('is-hidden');
        lenis.stop(); // Freeze Lenis scroll when submenu is open
      }
    };

    desktopSubmenuLinks.forEach(navLink => {
      navLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const targetId = navLink.getAttribute('data-submenu');
        if (isSubmenuOpen && activeSubmenuId === targetId) {
          closeSubmenu();
        } else {
          openSubmenu(targetId, navLink);
        }
      });
    });

    siteOverlay.addEventListener('click', () => {
      if (isSubmenuOpen) closeSubmenu();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isSubmenuOpen) {
        closeSubmenu();
      }
    });
  }

  // Mobile Drawer Toggle & Screen Switching (< 992px)
  const mobileNavToggle = document.querySelector('.mobile-nav-toggle');
  const mobileDrawer = document.querySelector('.mobile-menu-drawer');
  const mobileDrawerClose = document.querySelector('.mobile-drawer-close');
  const mobileSubmenuTriggers = document.querySelectorAll('[data-mobile-submenu]');
  const btnBackMainList = document.querySelectorAll('.btn-back-main');
  const mobileMainScreen = document.querySelector('.mobile-menu-main');
  const mobileSubmenuScreens = document.querySelectorAll('.mobile-menu-submenu');

  if (mobileNavToggle && mobileDrawer) {
    // Ensure hamburger-bar spans exist if template didn't have them
    if (!mobileNavToggle.querySelector('.hamburger-bar')) {
      mobileNavToggle.innerHTML = '<span class="hamburger-bar"></span><span class="hamburger-bar"></span><span class="hamburger-bar"></span>';
    }

    // Helper to calculate exact header height for mobile drawer positioning
    const updateHeaderHeight = () => {
      if (siteHeader) {
        const h = siteHeader.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
      }
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    const openMobileDrawer = () => {
      isMobileDrawerOpen = true;
      updateHeaderHeight();
      mobileNavToggle.classList.add('is-active');
      mobileNavToggle.setAttribute('aria-expanded', 'true');
      mobileDrawer.classList.add('is-active');
      if (siteOverlay) siteOverlay.classList.add('is-active');
      if (siteHeader) {
        siteHeader.classList.add('is-sticky', 'is-mobile-menu-open');
        siteHeader.classList.remove('is-hidden');
      }
      if (mobileMainScreen) mobileMainScreen.classList.add('is-active');
      mobileSubmenuScreens.forEach(screen => screen.classList.remove('is-active'));
    };

    const closeMobileDrawer = () => {
      isMobileDrawerOpen = false;
      mobileNavToggle.classList.remove('is-active');
      mobileNavToggle.setAttribute('aria-expanded', 'false');
      mobileDrawer.classList.remove('is-active');
      if (siteOverlay && !isSubmenuOpen) siteOverlay.classList.remove('is-active');
      if (siteHeader) {
        siteHeader.classList.remove('is-mobile-menu-open');
        const currentScroll = (lenis && typeof lenis.scroll !== 'undefined') ? lenis.scroll : (window.scrollY || 0);
        if (currentScroll <= 100) {
          siteHeader.classList.remove('is-sticky');
        }
      }
      if (mobileMainScreen) mobileMainScreen.classList.add('is-active');
      mobileSubmenuScreens.forEach(screen => screen.classList.remove('is-active'));
    };

    mobileNavToggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isMobileDrawerOpen) {
        closeMobileDrawer();
      } else {
        openMobileDrawer();
      }
    });

    if (mobileDrawerClose) {
      mobileDrawerClose.addEventListener('click', closeMobileDrawer);
    }

    // Close when tapping outside the white menu card
    mobileDrawer.addEventListener('click', (e) => {
      if (!e.target.closest('.mobile-card')) {
        closeMobileDrawer();
      }
    });

    if (siteOverlay) {
      siteOverlay.addEventListener('click', () => {
        if (isMobileDrawerOpen) closeMobileDrawer();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isMobileDrawerOpen) {
        closeMobileDrawer();
      }
    });

    mobileSubmenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = trigger.getAttribute('data-mobile-submenu');
        const targetScreen = document.getElementById(`mobile-submenu-${targetId}`);
        if (targetScreen && mobileMainScreen) {
          mobileMainScreen.classList.remove('is-active');
          mobileSubmenuScreens.forEach(screen => screen.classList.remove('is-active'));
          targetScreen.classList.add('is-active');
          mobileDrawer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    });

    btnBackMainList.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        mobileSubmenuScreens.forEach(screen => screen.classList.remove('is-active'));
        if (mobileMainScreen) {
          mobileMainScreen.classList.add('is-active');
          mobileDrawer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    });
  }

  // GSAP ScrollTrigger for Home Parallax Stacked Layers Section
  const parallaxSection = document.querySelector('.home-parallax-section');
  if (parallaxSection) {
    const layers = Array.from(parallaxSection.querySelectorAll('.parallax-bg-layer'));
    const cardContents = Array.from(parallaxSection.querySelectorAll('.parallax-card-content'));
    const dots = Array.from(parallaxSection.querySelectorAll('.parallax-dots-unified .dot'));

    // Set initial clip-path and z-index for layers (revealing from bottom to top)
    layers.forEach((layer, idx) => {
      gsap.set(layer, {
        zIndex: idx + 1,
        clipPath: idx > 0 ? 'inset(100% 0% 0% 0%)' : 'inset(0% 0% 0% 0%)',
      });
    });

    // Set initial opacity positions for card contents (content 1 visible, others hidden)
    cardContents.forEach((content, idx) => {
      if (idx > 0) {
        gsap.set(content, { opacity: 0, pointerEvents: 'none' });
      } else {
        gsap.set(content, { opacity: 1, pointerEvents: 'auto' });
      }
    });

    // Create pinned ScrollTrigger timeline
    const parallaxTl = gsap.timeline({
      scrollTrigger: {
        trigger: parallaxSection,
        start: 'top top',
        end: `+=${Math.max(1, layers.length - 1) * 100}%`,
        pin: true,
        scrub: 0.5,
        anticipatePin: 1,
        onUpdate: (self) => {
          // Update active dot in unified legend based on scroll progress (0 to 1)
          const currentStep = Math.min(
            layers.length - 1,
            Math.round(self.progress * (layers.length - 1))
          );
          dots.forEach((dot, idx) => {
            if (idx === currentStep) {
              dot.classList.add('active');
            } else {
              dot.classList.remove('active');
            }
          });
        },
      },
    });

    // Animate layers 2 to 7 releasing clip-path from bottom to top (inset(100% 0% 0% 0%) -> inset(0% 0% 0% 0%))
    // AND sequentially fade out previous content to pure white card, then fade in next content
    for (let i = 1; i < layers.length; i++) {
      parallaxTl.to(layers[i], {
        clipPath: 'inset(0% 0% 0% 0%)',
        ease: 'none',
        duration: 1,
      });

      // 1. Fade OUT previous card content in first half of step (0 -> 0.45)
      if (cardContents[i - 1]) {
        parallaxTl.to(
          cardContents[i - 1],
          {
            opacity: 0,
            pointerEvents: 'none',
            duration: 0.45,
            ease: 'power1.out',
          },
          '<'
        );
      }

      // 2. Fade IN next card content in second half of step (0.55 -> 1.0) over clean white background
      if (cardContents[i]) {
        parallaxTl.to(
          cardContents[i],
          {
            opacity: 1,
            pointerEvents: 'auto',
            duration: 0.45,
            ease: 'power1.in',
          },
          '<0.55'
        );
      }
    }
  }

  // 2. CRITICAL IMAGES PRELOADER & BODY FADE-IN
  const preloadImages = document.querySelectorAll('img[data-preload]');
  let hasRevealed = false;

  const revealPage = () => {
    if (hasRevealed) return;
    hasRevealed = true;

    // Body opacity 0 -> 1
    gsap.to(document.body, {
      opacity: 1,
      duration: 1,
      onComplete: () => {
        ScrollTrigger.refresh();
        document.body.classList.add('is-loaded');

        // Play the pre-prepared hero timeline after body fade-in completes
        if (heroTl) {
          heroTl.play();
        }
      },
    });
  };

  if (preloadImages.length > 0) {
    const promises = Array.from(preloadImages).map((img) => {
      return new Promise((resolve) => {
        const handleImageReady = () => {
          if ('decode' in img) {
            img.decode().then(resolve).catch(resolve);
          } else {
            resolve();
          }
        };

        if (img.complete && img.naturalHeight !== 0) {
          handleImageReady();
        } else {
          img.addEventListener('load', handleImageReady, { once: true });
          img.addEventListener('error', handleImageReady, { once: true });
        }
      });
    });

    Promise.all(promises).then(revealPage);
  } else {
    revealPage();
  }

  // Safety Fallback Timeout (5s max wait)
  setTimeout(revealPage, 5000);

  // GSAP Smooth Height Toggle for Expanded Filters Panel
  const filterToggleBtn = document.querySelector('.btn-filter-toggle');
  const expandedFiltersPanel = document.querySelector('.expanded-filters-panel');

  if (filterToggleBtn && expandedFiltersPanel) {
    let isFiltersOpen = false;

    filterToggleBtn.addEventListener('click', () => {
      if (!isFiltersOpen) {
        gsap.to(expandedFiltersPanel, {
          height: 'auto',
          opacity: 1,
          duration: 0.45,
          ease: 'power2.out',
        });
        filterToggleBtn.classList.add('active');
        isFiltersOpen = true;
      } else {
        gsap.to(expandedFiltersPanel, {
          height: 0,
          opacity: 0,
          duration: 0.35,
          ease: 'power2.inOut',
        });
        filterToggleBtn.classList.remove('active');
        isFiltersOpen = false;
      }
    });
  }

  // Fast Filters: pills are real <a> links with server-rendered href + active class.
  // No click interception needed — let the browser navigate normally.

  // Hero Intro "Leggi di più" GSAP Expand / Collapse Toggle
  const readMoreLinks = document.querySelectorAll('.hero-readmore .readmore-link');

  readMoreLinks.forEach((readMoreLink) => {
    const heroSection = readMoreLink.closest('.hero-esperienze') || document;
    const heroIntroCollapsible = heroSection.querySelector('.hero-intro.is-collapsible');

    if (!heroIntroCollapsible) return;

    let isIntroExpanded = false;
    let collapsedHeight = 0;

    readMoreLink.addEventListener('click', (e) => {
      e.preventDefault();

      if (!isIntroExpanded) {
        collapsedHeight = heroIntroCollapsible.offsetHeight;
        heroIntroCollapsible.classList.add('is-expanded');
        const fullHeight = heroIntroCollapsible.scrollHeight;

        gsap.fromTo(
          heroIntroCollapsible,
          { height: collapsedHeight },
          {
            height: fullHeight,
            duration: 0.55,
            ease: 'power2.out',
            onComplete: () => {
              gsap.set(heroIntroCollapsible, { height: 'auto' });
            },
          }
        );

        readMoreLink.textContent = 'Leggi meno';
        isIntroExpanded = true;
      } else {
        gsap.to(heroIntroCollapsible, {
          height: collapsedHeight,
          duration: 0.45,
          ease: 'power2.inOut',
          onComplete: () => {
            heroIntroCollapsible.classList.remove('is-expanded');
            gsap.set(heroIntroCollapsible, { height: '' });
          },
        });

        readMoreLink.textContent = 'Leggi di più';
        isIntroExpanded = false;
      }
    });
  });

  // Prevent Lenis scroll conflicts on all Swiper & Drag carousels and their parent containers
  document.querySelectorAll('.swiper, .drag-carousel').forEach((carouselEl) => {
    carouselEl.setAttribute('data-lenis-prevent', 'true');
    const parentSection = carouselEl.closest('section') || carouselEl.parentElement;
    if (parentSection) {
      parentSection.setAttribute('data-lenis-prevent', 'true');
    }
  });

  // Initialize Drag Carousel (.drag-carousel)
  const dragCarousels = document.querySelectorAll('.drag-carousel');
  dragCarousels.forEach((carousel) => {
    DragCarousel(carousel);
  });

  // Initialize Altro dal Fornitore / Card Swiper Sliders with Free Mode Drag & Touch Swipe
  const altreEsperienzeSwiperEls = document.querySelectorAll('.altre-esperienze-swiper');
  altreEsperienzeSwiperEls.forEach((swiperEl) => {
    new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      freeMode: {
        enabled: true,
        sticky: false,
        momentumBounce: true,
      },
      mousewheel: false, // Disabled mousewheel forceToAxis hijacking to prevent breaking vertical page scroll
      grabCursor: true,
      breakpoints: {
        576: {
          spaceBetween: 20,
        },
        768: {
          spaceBetween: 24,
        },
        1200: {
          spaceBetween: 28,
        },
      },
    });
  });

  // Card Prenota (single esperienza feratel): fixed in basso (< lg) quando
  // il bottom della card + 1rem tocca il bottom del viewport
  document.querySelectorAll('.card-prenota-wrapper[data-fix-bottom]').forEach((wrapper) => {
    const card = wrapper.querySelector('.card-prenota');
    if (!card) return;

    const mqMobile = window.matchMedia('(max-width: 991.98px)');
    let ticking = false;

    const unfix = () => {
      wrapper.classList.remove('is-fixed');
      wrapper.style.height = '';
      card.style.left = '';
      card.style.width = '';
    };

    const update = () => {
      ticking = false;

      if (!mqMobile.matches) {
        unfix();
        return;
      }

      const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const isFixed = wrapper.classList.contains('is-fixed');
      const cardHeight = isFixed ? wrapper.offsetHeight : card.offsetHeight;
      const rect = wrapper.getBoundingClientRect(); // wrapper resta come placeholder

      if (rect.top + cardHeight + remPx <= window.innerHeight) {
        wrapper.style.height = `${cardHeight}px`;
        card.style.left = `${rect.left}px`;
        card.style.width = `${rect.width}px`;
        wrapper.classList.add('is-fixed');
      } else if (isFixed) {
        unfix();
      }
    };

    const requestUpdate = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    lenis.on('scroll', requestUpdate);
    update();
  });

  // Auto-wrap card categories separator in <span class="separator"> if raw • is present
  document.querySelectorAll('.card-categories').forEach((el) => {
    if (el.textContent.includes('•') && !el.querySelector('.separator')) {
      el.innerHTML = el.innerHTML.replace(/\s*•\s*/g, ' <span class="separator">•</span> ');
    }
  });

  // Initialize AJAX Filtered Grid (Load More + filters)
  initFilteredGrid();

  // Initialize the footer language dropdown
  initFooterLanguageSwitcher();
});
